// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Validate environment variables
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  console.error('Missing Supabase environment variables:', {
    hasUrl: !!SUPABASE_URL,
    hasKey: !!SUPABASE_PUBLISHABLE_KEY,
  });
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL || '', SUPABASE_PUBLISHABLE_KEY || '', {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'x-client-info': 'github-page-connect',
    },
  },
});

// Helper function to ensure session is valid before calling functions
export async function ensureSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) {
    // Try to refresh the session
    const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshedSession) {
      throw new Error('No valid session. Please sign in again.');
    }
    return refreshedSession;
  }
  return session;
}

// Wrapper for invoking Edge Functions. Uses the Supabase client's session; only refresh when missing or token expired/near expiry to avoid 429 rate limits.
export async function invokeFunction(functionName: string, options?: { body?: any }) {
  const sessionResult = await supabase.auth.getSession();
  let session = sessionResult.data.session;

  if (!session) {
    const refreshResult = await supabase.auth.refreshSession();
    session = refreshResult.data.session;
    if (!session) {
      throw new Error('Authentication required. Please sign in again.');
    }
  }

  if (!session.access_token) {
    throw new Error('Invalid session. Please sign in again.');
  }

  // Only refresh if token is expired or expires within 60s (avoid calling refresh on every request → 429)
  const payload = session.access_token.split('.')[1];
  if (payload) {
    try {
      const { exp } = JSON.parse(atob(payload)) as { exp?: number };
      if (exp && exp * 1000 - Date.now() < 60_000) {
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData.session) session = refreshData.session;
      }
    } catch {
      // use current session
    }
  }

  const activeSession = session;

  const { data, error } = await supabase.functions.invoke(functionName, {
    body: { ...options?.body, _authToken: activeSession.access_token },
    headers: {
      Authorization: `Bearer ${activeSession.access_token}`,
    },
  });

  if (error) {
    const errorData = typeof error === 'object' && error !== null
      ? { ...error, status: (error as { status?: number }).status }
      : { message: String(error), status: 500 };
    let message =
      (errorData as { message?: string }).message ||
      (errorData as { error?: string }).error ||
      'Request failed';
    // Supabase FunctionsHttpError may put the response in .context (Response); parse body for server error message
    const errWithContext = error as { context?: { json?: () => Promise<unknown> } };
    if (typeof errWithContext?.context?.json === 'function') {
      try {
        const body = (await errWithContext.context.json()) as { error?: string; message?: string };
        if (body?.error || body?.message) message = body.error || body.message || message;
      } catch {
        // ignore
      }
    }
    const enriched = { ...errorData, message };
    console.error(`Function ${functionName} returned error:`, enriched);
    return { data: null, error: enriched };
  }

  return { data, error: null };
}